import { Box, Drawer, styled, Typography } from '@mui/material';
import { Row } from 'further-ui/components/Layout';
import { PropsWithChildren } from 'react';
import CloseButton from './CloseButton';

const drawerSize = {
  sm: 600,
  md: 800,
  lg: 1100,
  xl: 1400,
};

const DrawerContainer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: 'sm' | 'md' | 'lg' | 'xl' }>(({ theme, size }) => ({
  width: '100%',
  maxWidth: drawerSize[size],
  zIndex: 2000,
  '& .MuiDrawer-paper': {
    width: '100%',
    maxWidth: drawerSize[size],
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
  },
}));

const DrawerTitle = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
}));

const DrawerContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'innerScroll',
})<{ innerScroll: boolean }>(({ theme, innerScroll }) => ({
  padding: theme.spacing(0, theme.spacingSizes.lg),
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  overflowY: innerScroll ? 'auto' : undefined,
}));

const DrawerActions = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
}));

type Props = {
  open: boolean;
  onClose?: () => void;
  title: React.ReactNode;
  titleAdornment?: React.ReactNode;
  actions: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  innerScroll?: boolean;
};

const FullPageDrawer: React.FC<PropsWithChildren<Props>> = ({
  open,
  title,
  titleAdornment,
  onClose,
  children,
  actions,
  size = 'md',
  innerScroll = false,
}) => {
  return (
    <DrawerContainer
      anchor="right"
      open={open}
      onClose={() => {
        onClose?.();
      }}
      size={size}
    >
      <DrawerTitle centered justify="space-between">
        {typeof title === 'string' ? (
          <>
            <Row centered spacing="xs">
              <Typography variant="h4">{title}</Typography>
              {titleAdornment}
            </Row>
            <CloseButton onClose={onClose} />
          </>
        ) : (
          title
        )}
      </DrawerTitle>
      <DrawerContent innerScroll={innerScroll}>{children}</DrawerContent>
      <DrawerActions>{actions}</DrawerActions>
    </DrawerContainer>
  );
};

export default FullPageDrawer;
